// src/components/RfiForm.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import api from "../../api/axiosConfig";
import { useRolePermissions } from "../Settings/RoleContext";
import { ProjectContext } from "../../App";
import { MultiSelect } from "react-multi-select-component";
import { IoEyeOutline } from "react-icons/io5";
import { RiDeleteBin6Line } from "react-icons/ri";
import { ImUpload } from "react-icons/im";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdKeyboardArrowRight,
} from "react-icons/md";
import { motion } from "framer-motion";
import format from "date-fns/format";
export default function RfiForm({
  userDetails,
  rfi,
  setEditingRfi,
  onSuccess,
  rfiCount,
  rfiNumber,
  response,
  setResponse,
}) {
  const { hasPermission } = useRolePermissions();
  const { dialBoxopen } = useContext(ProjectContext);
  const [openDialog, setOpenDialog] = useState(false);
  const { projectId } = useParams();
  const [phases, setPhases] = useState([]);
  const [rfiCategories, setRfiCategories] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [rfiQueryToDelete, setRfiQueryToDelete] = useState([]);
  const [isQueryCheckboxChecked, setIsQueryCheckboxChecked] = useState(false);
  const [deleteQueryDialogOpen, setDeleteQueryDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [successMessage, setSuccessMessage] = useState({
    queryId: null,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);
  const inputRef = useRef();
  const [files, setFiles] = useState([]);
  const textareaRefs = useRef([]);
  const [activeQueryIdx, setActiveQueryIdx] = useState(null);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [form, setForm] = useState({
    rfi_number: "RFI-",
    rev: 0,
    phase_id: "",
    subject: "",
    description: "",
    queries: [
      {
        query_text: "",
        id: null,
        status: "Open",
        raised_by: userDetails.id,
        responses: [
          {
            rev: 0,
            response: "",
            responded_by: "",
            responded_date: null,
            discipline_id: "",
            company_name: "",
          },
        ],
      },
    ],
    discipline_id: [],
    other_discipline: "",
    category_id: [],
    other_category: "",
    requested_by: [],
    reviewed_by: [],
    approved_by: [],
    copy_to: [],
    requested_date: "",
    due_date: "",
    responded_date: "",
    due_date: "",
    cost_impact: "unknown",
    schedule_impact: "unknown",
    priority: "normal",
    status: "Open",
  });
  const [groupedQueries, setGroupedQueries] = useState([]);
  useEffect(() => {
    setGroupedQueries(groupQueries(form.queries));
  }, [form.queries]);
  useEffect(() => {
    if (rfi) {
      setOpenDialog(true);
    }
  }, [rfi]);
  useEffect(() => {
    if (userDetails.id) {
      setForm((prev) => ({
        ...prev,
        requested_by: [{ label: userDetails.name, value: userDetails.id }],
      }));
    }
  }, [userDetails]);

  const fetchData = async () => {
    try {
      const [catRes, desRes, phaseRes, userRes] = await Promise.all([
        api.get("/api/rficategories"),
        api.get("/api/disciplines"),
        api.get(`/api/project/${projectId}/phases`),
        api.get(`/api/projects/${projectId}/users`),
      ]);

      setRfiCategories(catRes.data || []);

      setDisciplines(desRes.data || []);

      setPhases(phaseRes.data?.phases); // ✅ handle either structure

      const activeUsers = userRes.data.filter(
        (member) => member.status !== "Inactive"
      );
      setUsers(activeUsers);
    } catch (error) {
      console.log("Error", error);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const handleInput = (e, idx) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    handleQueryChange(idx, "query_text", value);

    // Text before cursor
    const textBeforeCursor = value.slice(0, cursorPos);

    // Trigger dropdown when user types #
    if (textBeforeCursor.endsWith("#")) {
      setActiveQueryIdx(idx);
      setShowFileDropdown(true);
    } else {
      setShowFileDropdown(false);
    }
  };
  const insertFileReference = (idx, fileName) => {
    const textarea = textareaRefs.current[idx];
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const text = form.queries[idx].query_text || "";

    // Replace trigger '#' with file reference
    const newText =
      text.slice(0, cursorPos - 1) + ` [${fileName}] ` + text.slice(cursorPos);

    handleQueryChange(idx, "query_text", newText);

    // Move cursor after inserted file reference
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd =
        cursorPos + fileName.length + 1; // +1 for brackets adjustment
    }, 0);
  };

  const handleQueryChange = (index, field, value) => {
    const newQueries = [...form.queries];
    newQueries[index][field] = value;
    setForm((prev) => ({ ...prev, queries: newQueries }));
  };
  // For individual response fields
  const handleResponseChange = (queryIdx, respIdx, field, value) => {
    setForm((prev) => {
      const updated = [...prev.queries];
      updated[queryIdx].responses[respIdx][field] = value;
      return { ...prev, queries: updated };
    });
  };
  // Add new response to a query
  const addResponse = (queryIdx) => {
    setSuccessMessage({
      queryId: null,
      message: "",
    });

    setForm((prev) => {
      const updated = [...prev.queries];

      // ❌ If already has an empty (unsaved) response → DO NOT ADD
      const hasDraft = updated[queryIdx].responses.some((r) => !r.id);
      if (hasDraft) return prev;

      // ✅ Add new draft response
      updated[queryIdx].responses.push({
        response: "",
        responded_by: "",
        responded_date: null,
        entered_by: userDetails?.id,
      });

      return { ...prev, queries: updated };
    });
  };

  const addQuery = () => {
    setForm((prev) => {
      const queries = prev.queries || [];
      // Get only root-level "Q{n} Rev 0" parents
      const parentQueries = queries.filter((q) =>
        q.qNo?.match(/^Q\d+\s+(R|Rev)\s*0$/i)
      );

      // Extract parent numbers from Q{n} Rev 0
      const parentNumbers = parentQueries.map((q) => {
        const match = q.qNo.match(/^Q(\d+)\s+(R|Rev)\s*0$/i);
        return match ? parseInt(match[1], 10) : 0;
      });

      // Determine next Q number
      const nextQNumber =
        parentNumbers.length > 0 ? Math.max(...parentNumbers) + 1 : 1;

      const nextQNo = `Q${nextQNumber} R0`;
      const newQuery = {
        uid: crypto.randomUUID(),
        id: null,
        query_text: "",
        status: "Open",
        qNo: nextQNo,
        responses: [
          {
            response: "",
            responded_by: "",
            responded_date: null,
          },
        ],
      };

      setRfiQueriesPerPage(20);
      setCurrentPage(1);
      return {
        ...prev,
        queries: [...queries, newQuery],
      };
    });
  };
  const removeQuery = () => {
    setForm((prev) => ({
      ...prev,
      queries: prev.queries.slice(0, -1), // removes the last element
    }));
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const resetForm = () => {
    setForm({
      rfi_number: "RFI-",
      rev: 0,
      phase_id: "",
      subject: "",
      description: "",
      queries: [
        {
          query_text: "",
          id: null,
          status: "Open",
          responses: [
            {
              revision: 0,
              response: "",
              responded_by: "",
              responded_date: null,
            },
          ],
        },
      ],
      discipline_id: [],
      other_category: "",
      other_discipline: "",
      category_id: [],
      requested_by: [{ label: userDetails.name, value: userDetails.id }],
      reviewed_by: [],
      approved_by: [],
      copy_to: [],
      requested_date: "",
      due_date: "",
      cost_impact: "unknown",
      schedule_impact: "unknown",
      priority: "normal",
      status: "Open",
    });
    setSuccessMessage({
      queryId: null,
      message: "",
    });
    setFiles([]);
    setFileList([]);
    setEditingRfi(null);
    setCurrentPage(1);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.queries.length === 0) {
      alert("Fill all the fields");
      return;
    }
    const hasEmptyQuery = form.queries.some(
      (q) => !q.query_text || q.query_text.trim() === ""
    );
    const validateUsers = (arr) =>
      !arr || arr.length === 0 || arr.some((u) => !u || !u.value);

    const hasEmptyUsers =
      validateUsers(form.requested_by) ||
      validateUsers(form.reviewed_by) ||
      validateUsers(form.approved_by);

    if (hasEmptyUsers) {
      alert("Requested by, Reviewed by, and Approved by cannot be empty.");
      return;
    }

    if (hasEmptyQuery) {
      alert(
        "Query text cannot be empty. Please fill the query before submitting."
      );
      return;
    }
    const fixedForm = {
      ...form,
      queries: form.queries.map((q) => ({
        ...q,
        raised_by: q.raised_by || userDetails?.id, // <-- inject raised_by
      })),
    };
    const frontendURL = window.location.origin;
    try {
      const method = rfi ? "put" : "post";
      const url = rfi
        ? `/api/rfis/${rfi.id}`
        : `/api/project/rfi/${projectId}/rfis`;
      setLoading(true);
      const response = await api({
        method,
        url,
        data: { fixedForm, frontendURL },
      });
      const createdId = response.data?.id;
      console.log("rfi created");
      if (!createdId) {
        console.error("No rfi id returned by backend", response.data);
        // handle accordingly
      } else {
        if (files && files.length > 0) {
          const fd = new FormData();
          for (let f of files) fd.append("files", f);

          await api.post(`/api/rfis/${createdId}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadProgress(percent);
            },
          });
        }
      }
      console.log("rfi after else");
      setOpenDialog(false);
      resetForm();
      console.log("rfi after resetForm");

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error saving RFI:", err.response || err);
    } finally {
      setOpenDialog(false);
      setLoading(false);
      setUploadProgress(0);
    }
  };
  const handleFiles = (files) => {
    const newFiles = Array.from(files).map((file) => ({
      file,
      file_name: file.name,
      size: file.size,
      file_lastModified: file.lastModified,
      preview: URL.createObjectURL(file),
      isImage: file.type.startsWith("image"),
      isPdf: file.type === "application/pdf",
    }));

    // Push directly into the array (not inside arr)
    setFileList((prev) => [...prev, ...newFiles]);

    // Raw files as well
    setFiles((prev) => [...prev, ...newFiles.map((x) => x.file)]);
  };

  const removeFile = (idx) => {
    const updated = fileList.filter((_, i) => i !== idx);
    setFileList(updated);
    setFiles(updated.map((x) => x.file));
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleFiles(e.dataTransfer.files);
  }; // Cleanup object URLs
  useEffect(() => {
    return () => fileList.forEach((x) => URL.revokeObjectURL(x.preview));
  }, [fileList]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rfiQueriesPerPage, setRfiQueriesPerPage] = useState(() => {
    return Number(localStorage.getItem("rfiQueriesPerPage")) || 5;
  });
  useEffect(() => {
    localStorage.setItem("rfiQueriesPerPage", rfiQueriesPerPage);
  }, [rfiQueriesPerPage]);
  const totalPages = Math.ceil(rfi?.queries.length / rfiQueriesPerPage);
  const indexOfLastRfi = currentPage * rfiQueriesPerPage;
  const indexOfFirstRfi = indexOfLastRfi - rfiQueriesPerPage;
  const calculatePaginationCount = (rfiQueriesPerPage, currentPage) => {
    const start = (currentPage - 1) * rfiQueriesPerPage + 1;
    const end = start + rfiQueriesPerPage - 1;
    return `${start}-${end}`;
  };
  const paginationCount = calculatePaginationCount(
    rfiQueriesPerPage,
    currentPage
  );
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };
  const handleFirstPage = () => {
    if (currentPage > 1) setCurrentPage(1);
  };
  const handleLastPage = () => {
    if (currentPage < totalPages) setCurrentPage(totalPages);
  };

  const fetchRfiForm = () => {
    // 🧩 Helper to order queries
    function buildOrderedQueries(allQueries) {
      const orderedList = [];

      // Get top-level queries (parent_query_id = null)
      const parents = allQueries
        .filter((q) => !q.parent_query_id)
        .sort((a, b) => a.id - b.id);

      parents.forEach((parent, parentIndex) => {
        const parentNo = `Q${parentIndex + 1}`;

        // Push parent with Rev 0
        orderedList.push({
          ...parent,
          qNo: `${parentNo} R0`,
        });

        // Get direct children of this parent
        const children = allQueries
          .filter((q) => q.parent_query_id === parent.id)
          .sort((a, b) => a.id - b.id); // ordered by creation

        // Add children: Rev 1, Rev 2, Rev 3...
        children.forEach((child, childIndex) => {
          const childRev = childIndex + 1;

          orderedList.push({
            ...child,
            qNo: `${parentNo} R${childRev}`,
          });
        });
      });

      return orderedList;
    }

    // 📌 Build ordered list or default empty query
    let orderedQueries =
      rfi.queries && rfi.queries.length > 0
        ? buildOrderedQueries(rfi.queries)
        : [
            {
              id: null,
              query_text: "",
              status: "Open",
              raised_by: userDetails.id,
              responses: [
                {
                  response: "",
                  responded_by: "",
                  responded_date: null,
                },
              ],
              qNo: `${rfi.rfi_number}.1`,
            },
          ];

    // 🔥 Filter based on rfiQueriesPerPage count (pagination)
    if (rfiQueriesPerPage && Number(rfiQueriesPerPage) > 0) {
      orderedQueries = orderedQueries.slice(indexOfFirstRfi, indexOfLastRfi);
    }

    // 🧩 Normalize discipline & category values
    const disciplineValues = Array.isArray(rfi.disciplines)
      ? rfi.disciplines.map((d) => ({ label: d.name, value: d.id }))
      : [];

    const categoryValues = Array.isArray(rfi.categories)
      ? rfi.categories.map((c) => ({ label: c.name, value: c.id }))
      : [];

    const requestedByValues = Array.isArray(rfi.requested_by_users)
      ? rfi.requested_by_users.map((c) => ({ label: c.name, value: c.id }))
      : [];
    const approvedByValues = Array.isArray(rfi.approved_by_users)
      ? rfi.approved_by_users.map((c) => ({ label: c.name, value: c.id }))
      : [];
    const reviewedByValues = Array.isArray(rfi.reviewed_by_users)
      ? rfi.reviewed_by_users.map((c) => ({ label: c.name, value: c.id }))
      : [];
    const copyToValues = Array.isArray(rfi.copy_to_users)
      ? rfi.copy_to_users.map((c) => ({ label: c.name, value: c.id }))
      : [];
    setFileList(rfi.documents);
    // 📝 Set form
    setForm((prev) => ({
      ...prev,
      rfi_number: rfi.rfi_number || "",
      rev: rfi.rev || 0,
      phase_id: rfi.phase_id || "",
      subject: rfi.subject || "",
      description: rfi.description || "",
      queries: orderedQueries.map((q) => ({
        id: q.id,
        qNo: q.qNo,
        revision: q.rev,
        parent_query_id: q.parent_query_id,
        query_text: q.query_text || "",
        status: q.status || "Open",

        responses:
          q.responses && q.responses.length > 0
            ? q.responses
            : [
                {
                  response: q.response || "",
                  responded_by: q.responded_by || "",
                  responded_date: q.responded_date || null,
                },
              ],
      })),
      discipline_id: disciplineValues,
      other_discipline: rfi.other_discipline || "",
      other_category: rfi.other_category || "",
      category_id: categoryValues,
      requested_by: requestedByValues,
      reviewed_by: reviewedByValues,
      approved_by: approvedByValues,
      copy_to: copyToValues,
      requested_date: rfi.requested_date || "",
      due_date: rfi.due_date || "",
      cost_impact: rfi.cost_impact,
      schedule_impact: rfi.schedule_impact,
      priority: rfi.priority,
      status: rfi.status || "Open",
    }));
  };
  useEffect(() => {
    if (!rfi) return;
    if (rfi) {
      fetchRfiForm();
    }
  }, [rfi, rfiQueriesPerPage, currentPage]);
  const handleSetDeleteQuery = (e, id, groupedQueries) => {
    const isChecked = e.target.checked;

    // 1. Find the group that contains this id
    const group = groupedQueries.find(
      (g) => g.parent.id === id || g.children.some((c) => c.id === id)
    );

    if (!group) {
      console.error("Group not found for id:", id);
      return;
    }

    // 2. Build parentId and childIds from this group
    const parentId = group.parent?.id;
    const childIds = group.children?.map((c) => c.id) || [];

    setRfiQueryToDelete((prev) => {
      const updated = new Set(prev);

      // If clicked parent
      if (id === parentId) {
        if (isChecked) {
          updated.add(parentId);
          childIds.forEach((cId) => updated.add(cId));
        } else {
          updated.delete(parentId);
          childIds.forEach((cId) => updated.delete(cId));
        }
      }

      // If clicked child
      else {
        if (isChecked) {
          updated.add(id);

          // If all children selected → check parent
          const allSelected = childIds.every((cId) => updated.has(cId));
          if (allSelected) updated.add(parentId);
        } else {
          // Child unchecked
          updated.delete(id); // remove that child
          updated.delete(parentId); // remove parent

          // ❗ NEW — uncheck all sibling children
          childIds.forEach((cId) => updated.delete(cId));
        }
      }

      return [...updated];
    });
  };

  const handleDeleteQuery = async (id) => {
    try {
      await api.delete(`/api/rfi/query`, {
        data: { queryIds: rfiQueryToDelete },
      });
      setForm((prev) => ({
        ...prev,
        queries: prev.queries.filter((q) => !rfiQueryToDelete.includes(q.id)),
      }));
      setDeleteQueryDialogOpen(false);
      setIsQueryCheckboxChecked(false);
      setRfiQueryToDelete([]);
    } catch (err) {
      console.error("Error deleting RFI query:", err);
      const message =
        err.response?.data?.message ||
        "Failed to delete query. Please try again later.";
      alert(message); // 👈 shows the message from backend
    }
  };
  const autoResize = (e) => {
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };
  useEffect(() => {
    const expandContent = () => {
      const textareas = document.querySelectorAll(".auto-resize");
      const inputs = document.querySelectorAll(".auto-input");
      const selects = document.querySelectorAll(".auto-select");
      // helper to create measuring span
      const createMeasurer = (element) => {
        const span = document.createElement("span");
        const cs = window.getComputedStyle(element);
        span.style.visibility = "hidden";
        span.style.whiteSpace = "pre";
        span.style.position = "absolute";
        span.style.top = "-9999px";
        span.style.left = "-9999px";
        span.style.font = cs.font;
        span.style.letterSpacing = cs.letterSpacing;
        span.style.padding = cs.padding;
        span.style.border = cs.border;
        document.body.appendChild(span);
        return span;
      };
      textareas.forEach((ta) => {
        ta.style.height = "auto";
        ta.style.height = ta.scrollHeight + "px";
      });
      // Auto width for input
      inputs.forEach((input) => {
        // Create hidden span
        const span = document.createElement("span");
        span.style.visibility = "hidden";
        span.style.whiteSpace = "pre";
        span.style.font = window.getComputedStyle(input).font;
        span.style.padding = window.getComputedStyle(input).padding;

        // Set span text as input value
        span.textContent = input.value || " ";

        document.body.appendChild(span);

        // Update width
        input.style.width = span.offsetWidth + 10 + "px"; // +10px buffer

        span.remove();
      });
      // Auto width for selects (use selected option text)
      selects.forEach((sel) => {
        const span = createMeasurer(sel);
        const opt = sel.options[sel.selectedIndex];
        span.textContent = (opt && opt.text) || sel.placeholder || " ";

        const width = Math.max(span.offsetWidth, 20);
        sel.style.width = width + 28 + "px"; // extra buffer for dropdown arrow
        span.remove();
      });
    };
    if ((form, form.queries.length > 0)) {
      expandContent();
    }
  }, [form, form.queries]);

  // function groupQueries(queries) {
  //   const groups = {};

  //   // Step 1 — collect parents
  //   queries.forEach((q) => {
  //     if (!q.parent_query_id) {
  //       groups[q.id] = { parent: q, children: [] };
  //     }
  //   });

  //   // Step 2 — attach children
  //   queries.forEach((q) => {
  //     if (q.parent_query_id) {
  //       if (!groups[q.parent_query_id]) {
  //         groups[q.parent_query_id] = { parent: null, children: [] };
  //       }
  //       groups[q.parent_query_id].children.push(q);
  //     }
  //   });

  //   return Object.values(groups);
  // }
  function groupQueries(queries) {
    const groups = {};

    queries.forEach((q) => {
      console.log("q", q);
      const key = q.id ?? q.uid; // UNIQUE KEY

      if (!q.parent_query_id) {
        groups[key] = { parent: q, children: [] };
      }
    });

    queries.forEach((q) => {
      if (q.parent_query_id) {
        const key = q.parent_query_id;
        if (!groups[key]) groups[key] = { parent: null, children: [] };
        groups[key].children.push(q);
      }
    });

    return Object.values(groups);
  }

  const handleResponse = async (queryId) => {
    const query = form.queries.find((q) => q.id === queryId);
    if (!query) return;

    // 🔥 Add entered_by only where missing
    const fixedResponses = query.responses.map((r) => ({
      ...r,
      entered_by: r.entered_by || userDetails.id,
    }));

    try {
      const res = await api.post(`/api/addresponse/${queryId}`, {
        responses: fixedResponses, // <-- send corrected array
      });

      const updatedResponses = res.data.data;

      // update UI
      setForm((prev) => ({
        ...prev,
        queries: prev.queries.map((q) =>
          q.id === queryId ? { ...q, responses: updatedResponses } : q
        ),
      }));
      setSuccessMessage({
        queryId,
        message: res.data.message,
      });
      if (!hasPermission("rfi", "update")) {
        setTimeout(() => {
          setSuccessMessage({
            queryId: null,
            message: "",
          });
        }, 1000);
      }
      onSuccess();
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <div>
      <div>
        {hasPermission("rfi", "create") && (
          <button
            className="rfi-log-add-btn"
            style={{
              marginTop: "0px",
              position: "relative",
              fontFamily: '"Lato", sans-serif',
            }}
            onClick={() => {
              setOpenDialog(true);
              setForm((prev) => ({
                ...prev,
                rfi_number: `RFI-` + String(rfiCount + 1).padStart(2, "0"),
              }));
              setResponse(false);
            }}
            title="Add Time Log"
          >
            Add RFI
          </button>
        )}
      </div>

      {openDialog && (
        <div
          className={`rfi-overlay-add-time-log ${
            dialBoxopen ? "open" : "close"
          }`}
          style={{ zIndex: "15" }}
        >
          <div className="w-[94%] left-[3.4%]  top-[2%]  relative  h-[86%]  overflow-y-auto  overflow-x-hidden rounded-lg border border-[#ddd] border-[0.5px]  flex flex-col  transform-gpu">
            <div className="font-normal bg-[#f5f5f5] p-2 pl-[1rem] h-[30px] items-center top-0 left-0 z-2 flex flex-row justify-between">
              <h3
                style={{
                  backgroundColor: "transparent",
                  margin: "0px",
                  fontSize: "15px",
                }}
              >
                {response
                  ? `Enter Response for ${form.rfi_number}`
                  : rfi
                  ? `Edit ${form.rfi_number}`
                  : "New RFI"}
              </h3>
              {rfiQueryToDelete.length > 0 && (
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setDeleteQueryDialogOpen(true)}
                  color="secondary"
                  variant="contained"
                  className="relative p-1 text-white bg-red-600 border-none rounded cursor-pointer"
                >
                  Delete {rfiQueryToDelete.length > 1 ? "Queries" : "Query"}
                </motion.button>
              )}
            </div>

            <form
              onSubmit={handleSubmit}
              className=" px-[1rem] h-[76vh] relative mb-[3rem] overflow-y-auto overflow-x-auto"
            >
              <div className="scrollable-t-content-rfi">
                <div className="grid items-start grid-cols-5 gap-4 auto-rows-auto">
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      RFI Number
                    </h3>
                    <input
                      type="text"
                      name="rfi_number"
                      placeholder="RFI Number"
                      value={form.rfi_number}
                      onChange={handleChange}
                      disabled={response || !hasPermission("rfi", "update")}
                      required
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] disabled:text-gray-500 rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Phase
                    </h3>
                    <select
                      name="phase_id"
                      value={form.phase_id}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      onChange={handleChange}
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd]  disabled:text-gray-500  rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    >
                      <option value="">Select Phase</option>
                      {phases?.map((phase) => (
                        <option key={phase.id} value={phase.id}>
                          {phase.phaseno}-{phase.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Subject
                    </h3>
                    <input
                      type="text"
                      name="subject"
                      placeholder="Subject"
                      value={form.subject}
                      onChange={handleChange}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      required
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] rounded-[3px]  disabled:text-gray-400 font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    />{" "}
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Disciplines
                    </h3>
                    <MultiSelect
                      value={form.discipline_id} // array of numbers
                      options={disciplines.map((d) => ({
                        label: d.name,
                        value: d.id, // number
                      }))}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      className={`${response ? "text-gray-400" : ""}`}
                      placeholder="Select disciplines"
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          discipline_id: e, // already array of numbers
                        }))
                      }
                    />
                    {form.discipline_id.some((d) => d.value === 8) && (
                      <input
                        className="p-[6px] border-[0.5px] mt-[15px] border-solid border-[#ddd]  disabled:text-gray-400 w-[95%] rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                        value={form.other_discipline}
                        name="other_discipline"
                        disabled={
                          response || (rfi && !hasPermission("rfi", "update"))
                        }
                        onChange={handleChange}
                        placeholder="Mention descipline name"
                      />
                    )}
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Category
                    </h3>

                    <MultiSelect
                      value={form.category_id} // array of numbers
                      options={rfiCategories.map((d) => ({
                        label: d.name,
                        value: d.id, // number
                      }))}
                      className={`${response ? "text-gray-400" : ""}`}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      placeholder="Select disciplines"
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          category_id: e, // already array of numbers
                        }))
                      }
                    />
                    {form.category_id.some((d) => d.value === 3) && (
                      <input
                        className="p-[6px] border-[0.5px] mt-[15px] border-solid border-[#ddd]  disabled:text-gray-700  w-[95%] rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                        value={form.other_category}
                        onChange={handleChange}
                        name="other_category"
                        disabled={
                          response || (rfi && !hasPermission("rfi", "update"))
                        }
                        placeholder="Mention category name"
                        style={{ marginTop: "15px" }}
                      />
                    )}
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Submitted on
                    </h3>
                    <DatePicker
                      type="date"
                      placeholderText="Select Date"
                      dateFormat="dd-MM-yyyy"
                      name="requested_date"
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      selected={form.requested_date}
                      onChange={(date) =>
                        setForm((prev) => ({
                          ...prev,
                          requested_date: date,
                        }))
                      }
                      popperPlacement="right"
                      className="pl-[5px] border border-[0.5px] cursor-pointer border-[#ddd] disabled:text-gray-400 border-solid h-[26px] w-[17vw] rounded-[3px]  text-[14px] focus:border-[#535353] focus:outline-none"
                    />{" "}
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Due on
                    </h3>
                    <DatePicker
                      type="date"
                      placeholderText="Select Date"
                      dateFormat="dd-MM-yyyy"
                      name="due_date"
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      selected={form.due_date}
                      onChange={(date) =>
                        setForm((prev) => ({
                          ...prev,
                          due_date: date,
                        }))
                      }
                      popperPlacement="right"
                      className="pl-[5px] border border-[0.5px] cursor-pointer border-[#ddd] border-solid disabled:text-gray-400 h-[26px] w-[17vw] rounded-[3px]  text-[14px] focus:border-[#535353] focus:outline-none"
                    />{" "}
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Prepared by
                    </h3>
                    <MultiSelect
                      value={
                        Array.isArray(form.requested_by)
                          ? form.requested_by
                          : []
                      }
                      options={users
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((u) => ({
                          label: u.name,
                          value: u.id,
                        }))}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      className={`${response ? "text-gray-400" : ""}`}
                      placeholder="Select Prepared by"
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          requested_by: e, // this is always an array
                        }))
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Reviewed by
                    </h3>
                    <MultiSelect
                      value={
                        Array.isArray(form.reviewed_by) ? form.reviewed_by : []
                      }
                      options={users
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((u) => ({
                          label: u.name,
                          value: u.id,
                        }))}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      className={`${response ? "text-gray-400" : ""}`}
                      placeholder="Select Prepared by"
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          reviewed_by: e, // this is always an array
                        }))
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Approved by
                    </h3>
                    <MultiSelect
                      value={
                        Array.isArray(form.approved_by) ? form.approved_by : []
                      }
                      options={users
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((u) => ({
                          label: u.name,
                          value: u.id,
                        }))}
                      className={`${response ? "text-gray-400" : ""}`}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      placeholder="Select Prepared by"
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          approved_by: e, // this is always an array
                        }))
                      }
                    />
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Cost impact
                    </h3>
                    <select
                      type="text"
                      name="cost_impact"
                      value={form.cost_impact}
                      onChange={handleChange}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      required
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    >
                      {" "}
                      <option>Select Cost impact</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Schedule impact
                    </h3>
                    <select
                      type="text"
                      name="schedule_impact"
                      value={form.schedule_impact}
                      onChange={handleChange}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      required
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    >
                      {" "}
                      <option>Select Schedule impact</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Priority
                    </h3>
                    <select
                      type="text"
                      name="priority"
                      value={form.priority}
                      onChange={handleChange}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      required
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    >
                      {" "}
                      <option>Select Priority</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Remarks
                    </h3>
                    <input
                      type="text"
                      name="description"
                      placeholder="Remarks"
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      value={form.description}
                      onChange={handleChange}
                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] rounded-[3px]  disabled:text-gray-400 font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col w-[17.5vw]">
                    <h3 className="font-normal text-black m-[3px] ml-0 text-[15px]">
                      Send RFI Copy To
                    </h3>
                    <MultiSelect
                      value={Array.isArray(form.copy_to) ? form.copy_to : []}
                      options={users
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((u) => ({
                          label: u.name,
                          value: u.id,
                        }))}
                      disabled={
                        response || (rfi && !hasPermission("rfi", "update"))
                      }
                      className={`${response ? "text-gray-400" : ""}`}
                      placeholder="Select Prepared by"
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          copy_to: e,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="flex w-full gap-2 mb-3">
                  <div className="w-[100%] pt-6 gap-4 flex">
                    <div className="flex flex-col w-full gap-2 p-4 border border-[#ddd] border-solid rounded-lg">
                      <div className="flex items-start justify-start w-full">
                        {!response && (
                          <div className="flex w-full">
                            {/* LEFT SIDE: DRAG & DROP BOX */}
                            <div
                              className={`
      flex flex-col items-center p-3 transition border-2 border-gray-400 bg-gray-50 
      border-dashed rounded-lg cursor-pointer h-[30vh]
      ${fileList.length === 0 ? "w-full" : "w-1/2"}
    `}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={handleDrop}
                              onClick={() => inputRef.current.click()}
                            >
                              <div className="flex flex-col items-center justify-center h-full gap-2 text-sm text-gray-600 bg-transparent">
                                <ImUpload className="w-8 h-8 text-gray-500 bg-transparent" />
                                <p className="m-0 bg-transparent">
                                  Drag & drop files here
                                </p>
                                <p className="m-0 bg-transparent">-OR-</p>
                                <span className="px-[10px] py-[6px] text-white rounded-[3px] bg-[#6200ee] cursor-pointer text-[13px]">
                                  Browse files
                                </span>
                              </div>
                            </div>

                            {/* RIGHT SIDE: FILE LIST OUTSIDE THE DRAG BOX */}
                            {fileList.length > 0 && (
                              <div className="w-1/2 h-[30vh] border border-gray-300 p-4 rounded-lg overflow-auto bg-white">
                                <div className="grid grid-cols-3 gap-4">
                                  {fileList.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex flex-col p-2 border rounded-md shadow bg-gray-50"
                                    >
                                      {item.type?.startsWith("image/") ? (
                                        <img
                                          src={item.preview}
                                          alt={item.file?.name}
                                          className="object-cover w-full h-32 border rounded-md"
                                        />
                                      ) : item.type === "application/pdf" ? (
                                        <iframe
                                          src={item.preview}
                                          className="w-full h-32 border rounded-md"
                                          title="PDF Preview"
                                        />
                                      ) : (
                                        // <div className="flex items-center justify-center h-32 text-gray-500">
                                        //   No preview
                                        // </div>
                                        <div></div>
                                      )}
                                      <div className="flex items-center justify-between w-full bg-transparent">
                                        <div className="flex flex-col items-start w-full bg-transparent">
                                          <p className="text-sm w-[12rem] text-start font-medium truncate mt-0 bg-transparent">
                                            {rfi
                                              ? item.file_name
                                              : item.file?.name}
                                          </p>
                                          <p className="m-0 text-xs text-gray-600 bg-transparent">
                                            {(
                                              (rfi
                                                ? item.size
                                                : item.file?.size) / 1024
                                            ).toFixed(1)}{" "}
                                            KB
                                          </p>
                                        </div>

                                        {!item.id && (
                                          <button
                                            className="text-sm text-red-500 bg-transparent border-none cursor-pointer hover:underline"
                                            onClick={() => removeFile(idx)}
                                            type="button"
                                          >
                                            <RiDeleteBin6Line size={20} />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Hidden Input */}
                        <input
                          type="file"
                          multiple
                          ref={inputRef}
                          className="hidden"
                          onChange={(e) => handleFiles(e.target.files)}
                        />
                      </div>
                      <div>
                        {loading && files.length > 0 && (
                          <div className="w-full mt-4">
                            <p>Uploading: {uploadProgress}%</p>
                            <div className="w-full h-3 overflow-hidden bg-gray-300 rounded">
                              <div
                                className="h-full transition-all bg-green-500"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}{" "}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  {!rfi &&
                    form.queries.map((q, idx) => (
                      <div
                        key={idx}
                        className={`flex flex-col gap-0 p-4 rounded-lg mb-[15px] bg-blue-100`}
                      >
                        <div className="flex gap-1 mb-2 bg-transparent">
                          {" "}
                          {hasPermission("rfi", "delete") &&
                            !response &&
                            rfi && (
                              <input
                                type="checkbox"
                                style={{ cursor: "pointer" }}
                                checked={rfiQueryToDelete.includes(q.id)}
                                onChange={(e) => handleSetDeleteQuery(e, q.id)}
                              />
                            )}
                          <h3 className="m-0 bg-transparent">
                            {rfi ? q.qNo : `Q${idx + 1}`}
                          </h3>
                          <span className="mt-1 ml-0.5 text-xs text-gray-500 bg-transparent">
                            Type # to mention the file name in the query.
                          </span>
                        </div>
                        {showFileDropdown &&
                          activeQueryIdx === idx &&
                          fileList.length > 0 && (
                            <div className="relative top-0 left-0 z-50 w-64 mt-1 overflow-auto bg-white border rounded shadow max-h-40">
                              {fileList.map((file, i) => (
                                <div
                                  key={i}
                                  className="px-2 py-1 text-sm cursor-pointer hover:bg-gray-100"
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // prevent textarea blur
                                    insertFileReference(idx, file.file_name);
                                    setShowFileDropdown(false); // hide after selection
                                  }}
                                >
                                  {file.file_name}
                                </div>
                              ))}
                            </div>
                          )}
                        <textarea
                          type="text"
                          value={q.query_text}
                          placeholder="Query"
                          ref={(el) => (textareaRefs.current[idx] = el)}
                          onChange={(e) => handleInput(e, idx)}
                          disabled={
                            q.status === "Closed" ||
                            response ||
                            (rfi && !hasPermission("rfi", "update"))
                          }
                          onInput={autoResize}
                          // ref={descRef}
                          className="auto-resize pl-[5px] border border-[#ddd] rounded-[3px] font-lato text-[14px] cursor-text w-auto h-auto  max-w-[100vw] focus:border-[#535353] focus:outline-none no-scrollbar"
                        />
                      </div>
                    ))}
                  {rfi &&
                    groupedQueries.map((group, gIdx) => (
                      <div
                        key={gIdx}
                        className="p-4 mb-4 border border-blue-300 rounded-lg bg-blue-50"
                      >
                        {group.parent && (
                          <div className="flex flex-col gap-0 p-2 mb-4 bg-blue-100 border border-blue-300 rounded-lg shadow-lg">
                            <div className="flex items-center gap-1 mb-2 bg-transparent">
                              {hasPermission("rfi", "delete") &&
                                !response &&
                                rfi && (
                                  <input
                                    type="checkbox"
                                    style={{ cursor: "pointer" }}
                                    checked={rfiQueryToDelete.includes(
                                      group.parent.id
                                    )}
                                    onChange={(e) =>
                                      handleSetDeleteQuery(
                                        e,
                                        group.parent.id,
                                        groupedQueries
                                      )
                                    }
                                  />
                                )}
                              <h5 className="m-0 bg-transparent text-[16px]">
                                {rfi ? (
                                  <p className="m-0 font-semibold bg-transparent">
                                    {group?.parent.qNo?.split(" ")[0]}{" "}
                                    <span className="text-xs font-normal text-black bg-transparent">
                                      R
                                      {group?.parent.qNo
                                        ?.split(" ")[1]
                                        .replace("R", "")}
                                    </span>
                                  </p>
                                ) : (
                                  `Q${gIdx + 1}`
                                )}
                              </h5>
                              <span className="mt-1 ml-0.5 text-xs text-gray-500 bg-transparent">
                                Type # to mention the file name in the query.
                              </span>
                            </div>
                            {showFileDropdown &&
                              activeQueryIdx ===
                                form.queries.findIndex(
                                  (x) => x.id === group.parent.id
                                ) &&
                              fileList.length > 0 && (
                                <div className="relative top-0 left-0 z-50 w-64 mt-1 overflow-auto bg-white border rounded shadow max-h-40">
                                  {fileList.map((file, i) => (
                                    <div
                                      key={i}
                                      className="px-2 py-1 text-sm cursor-pointer hover:bg-gray-100"
                                      onMouseDown={(e) => {
                                        e.preventDefault(); // prevent textarea blur
                                        insertFileReference(
                                          form.queries.findIndex(
                                            (x) => x.id === group.parent.id
                                          ),
                                          file.file_name
                                        );
                                        setShowFileDropdown(false); // hide after selection
                                      }}
                                    >
                                      {file.file_name}
                                    </div>
                                  ))}
                                </div>
                              )}
                            <textarea
                              value={group.parent.query_text}
                              placeholder="Query"
                              onChange={(e) => {
                                const parentKey =
                                  group.parent.id ?? group.parent.uid;

                                const index = form.queries.findIndex(
                                  (q) => (q.id ?? q.uid) === parentKey
                                );

                                handleInput(e, index);
                              }}
                              onInput={autoResize}
                              ref={(el) =>
                                (textareaRefs.current[
                                  form.queries.findIndex(
                                    (x) => x.id === group.parent.id
                                  )
                                ] = el)
                              }
                              disabled={
                                group.parent.status === "Closed" ||
                                response ||
                                (rfi && !hasPermission("rfi", "update"))
                              }
                              input={autoResize}
                              className={`auto-resize pl-[5px] border border-[#ddd] border-[0.5px] no-scrollbar rounded-[3px] font-lato text-[15px] w-auto min-h-0 h-auto max-h-auto  focus:border-[#535353] focus:outline-none  disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-gray-50  ${
                                group.parent.status === "Closed" ||
                                response ||
                                (rfi && !hasPermission("rfi", "update"))
                                  ? "resize-none"
                                  : ""
                              }`}
                            />

                            {rfi && (
                              <div className="p-2 pt-0 pb-2 mt-2 ml-5 rounded-lg bg-gray-50">
                                {group.parent.responses.map((r, rIdx) => (
                                  <div
                                    key={rIdx}
                                    className="flex flex-col h-auto gap-2 py-2 bg-transparent"
                                  >
                                    <div className="flex items-center gap-4 bg-transparent">
                                      <h5 className="m-0 text-gray-500 bg-transparent">
                                        Responded by
                                      </h5>

                                      <div
                                        className={`flex items-start justify-between   bg-transparent`}
                                      >
                                        {group.parent.status === "Closed" ||
                                        (r.id &&
                                          !hasPermission("rfi", "update") &&
                                          r.entered_by !== userDetails.id) ? (
                                          <span className="font-lato text-[13px] mr-4 text-[#0057D9] bg-transparent  whitespace-nowrap">
                                            {r.responded_by || ""}
                                          </span>
                                        ) : (
                                          <input
                                            value={r.responded_by}
                                            onChange={(e) =>
                                              handleResponseChange(
                                                form.queries.findIndex(
                                                  (x) =>
                                                    x.id === group.parent.id
                                                ),
                                                rIdx,
                                                "responded_by",
                                                e.target.value
                                              )
                                            }
                                            disabled={
                                              group.parent.status ===
                                                "Closed" || // Block everyone if closed
                                              (!hasPermission(
                                                "rfi",
                                                "update"
                                              ) &&
                                                r.id &&
                                                r.entered_by !== userDetails.id) // Only allow editing own response
                                            }
                                            className="p-[6px] border-[0.5px] border-solid border-[#ddd] w-[15vw] rounded-[3px] font-lato text-[13px] cursor-pointer focus:border-[#535353] focus:outline-none mr-2"
                                            placeholder="Responded by"
                                          />
                                        )}
                                        {group.parent.status === "Closed" ||
                                        (r.id &&
                                          !hasPermission("rfi", "update") &&
                                          r.entered_by !== userDetails.id) ? (
                                          // Plain text when disabled
                                          <span className="font-lato text-[13px] mr-4 bg-transparent  text-[#E4572E]">
                                            {r.responded_date
                                              ? format(
                                                  r.responded_date,
                                                  "dd-MM-yyyy"
                                                )
                                              : ""}
                                          </span>
                                        ) : (
                                          <DatePicker
                                            selected={r.responded_date}
                                            onChange={(date) =>
                                              handleResponseChange(
                                                form.queries.findIndex(
                                                  (x) =>
                                                    x.id === group.parent.id
                                                ),
                                                rIdx,
                                                "responded_date",
                                                date
                                              )
                                            }
                                            disabled={
                                              group.parent.status ===
                                                "Closed" || // Block everyone if closed
                                              (!hasPermission(
                                                "rfi",
                                                "update"
                                              ) &&
                                                r.id &&
                                                r.entered_by !== userDetails.id) // Only allow editing own response
                                            }
                                            placeholderText="Select Date"
                                            dateFormat="dd-MM-yyyy"
                                            className="pl-[5px] border-[0.5px] border-solid border-[#ddd] h-[26px] w-[10vw] rounded-[3px] font-lato text-[13px] focus:border-[#535353] focus:outline-none mr-2"
                                          />
                                        )}
                                        {group.parent.status === "Closed" ||
                                        (r.id &&
                                          !hasPermission("rfi", "update") &&
                                          r.entered_by !== userDetails.id) ? (
                                          // Plain text when disabled
                                          <span className="font-lato text-[13px] mr-4 bg-transparent  text-[#0E9F6E]">
                                            {r.discipline_name}
                                          </span>
                                        ) : (
                                          <select
                                            className={`p-[5px] border border-[#ddd] border-[0.5px] h-[30px] w-[15vw] rounded-[3px] focus:border-[#535353] focus:outline-none font-lato text-[13px] cursor-pointer  disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-gray-50 disabled:w-[auto] mr-2   ${
                                              group.parent.status ===
                                                "Closed" ||
                                              (r.id &&
                                                !hasPermission("rfi", "update"))
                                                ? "auto-select w-auto"
                                                : "w-[15vw]"
                                            }`}
                                            value={r.discipline_id}
                                            onChange={(e) =>
                                              handleResponseChange(
                                                form.queries.findIndex(
                                                  (x) =>
                                                    x.id === group.parent.id
                                                ),
                                                rIdx,
                                                "discipline_id",
                                                e.target.value
                                              )
                                            }
                                            disabled={
                                              group.parent.status ===
                                                "Closed" || // Block everyone if closed
                                              (!hasPermission(
                                                "rfi",
                                                "update"
                                              ) &&
                                                r.id &&
                                                r.entered_by !== userDetails.id) // Only allow editing own response
                                            }
                                          >
                                            <option value="">
                                              Select Descipline
                                            </option>
                                            {disciplines.map((d) => (
                                              <option value={d.id}>
                                                {d.name}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                        {group.parent.status === "Closed" ||
                                        (r.id &&
                                          !hasPermission("rfi", "update") &&
                                          r.entered_by !== userDetails.id) ? (
                                          // Show plain text when disabled
                                          <span className="font-lato text-[13px]  text-[#7B2CBF] bg-transparent ">
                                            {r.company_name || ""}
                                          </span>
                                        ) : (
                                          <input
                                            value={r.company_name}
                                            onChange={(e) =>
                                              handleResponseChange(
                                                form.queries.findIndex(
                                                  (x) =>
                                                    x.id === group.parent.id
                                                ),
                                                rIdx,
                                                "company_name",
                                                e.target.value
                                              )
                                            }
                                            disabled={
                                              group.parent.status ===
                                                "Closed" || // Block everyone if closed
                                              (!hasPermission(
                                                "rfi",
                                                "update"
                                              ) &&
                                                r.id &&
                                                r.entered_by !== userDetails.id) // Only allow editing own response
                                            }
                                            className="p-[6px] border-[0.5px] border-solid border-[#ddd] w-[15vw] rounded-[3px] font-lato text-[13px] cursor-pointer focus:border-[#535353] focus:outline-none"
                                            placeholder="Company Name"
                                          />
                                        )}
                                      </div>
                                    </div>

                                    <textarea
                                      value={r.response}
                                      onChange={(e) =>
                                        handleResponseChange(
                                          form.queries.findIndex(
                                            (x) => x.id === group.parent.id
                                          ),
                                          rIdx,
                                          "response",
                                          e.target.value
                                        )
                                      }
                                      onInput={autoResize}
                                      disabled={
                                        group.parent.status === "Closed" || // Block everyone if closed
                                        (!hasPermission("rfi", "update") &&
                                          r.id &&
                                          r.entered_by !== userDetails.id) // Only allow editing own response
                                      }
                                      placeholder="Response"
                                      className={`auto-resize pl-[5px] border border-[#ddd] border-[0.5px] no-scrollbar rounded-[3px] font-lato text-[15px] w-auto min-h-0 h-auto max-h-auto  focus:border-[#535353] focus:outline-none disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-gray-50  ${
                                        group.parent.status === "Closed" ||
                                        response ||
                                        (rfi && !hasPermission("rfi", "update"))
                                          ? "resize-none"
                                          : ""
                                      }`}
                                    />
                                  </div>
                                ))}

                                <div className="flex gap-2 w-[600px] bg-transparent">
                                  {group.parent.revision === 0 && (
                                    <select
                                      value={group.parent.status}
                                      onChange={(e) =>
                                        handleQueryChange(
                                          form.queries.findIndex(
                                            (x) => x.id === group.parent.id
                                          ),
                                          "status",
                                          e.target.value
                                        )
                                      }
                                      disabled={
                                        response ||
                                        !hasPermission("rfi", "update")
                                      }
                                      className="p-[6px] border-[0.5px] border-solid border-[#ddd] w-[30%]  rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                                    >
                                      <option value="Open">Open</option>
                                      <option value="Closed">Closed</option>
                                    </select>
                                  )}
                                  {group.parent.status !== "Closed" &&
                                    group.parent.responses.every(
                                      (r) => r.id
                                    ) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          addResponse(
                                            form.queries.findIndex(
                                              (x) => x.id === group.parent.id
                                            )
                                          );
                                        }}
                                        disabled={
                                          group.parent.status === "Closed"
                                        }
                                        className={`h-auto px-[10px] py-[6px] text-center border-none text-white rounded-[3px]   font-lato text-[13px] ${
                                          group.parent.status === "Closed"
                                            ? "bg-gray-500 cursor-not-allowed"
                                            : "bg-[#6200ee] cursor-pointer"
                                        }`}
                                      >
                                        Add Response
                                      </button>
                                    )}
                                  {group.parent.status !== "Closed" &&
                                    successMessage.queryId !==
                                      group.parent.id &&
                                    group.parent.responses.some(
                                      (r) =>
                                        r.responded_by &&
                                        r.responded_date &&
                                        r.response &&
                                        r.discipline_id &&
                                        r.company_name
                                    ) && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          handleResponse(group.parent.id);
                                          !hasPermission("rfi", "update") &&
                                            setOpenDialog(false);
                                        }}
                                        disabled={
                                          group.parent.status === "Closed"
                                        }
                                        className={`h-auto  px-[10px] py-[6px] text-center border-none text-white rounded-[3px]   font-lato text-[13px] ${
                                          group.parent.status === "Closed"
                                            ? "bg-gray-500 cursor-not-allowed"
                                            : "bg-[#6200ee] cursor-pointer"
                                        }`}
                                      >
                                        Submit
                                      </button>
                                    )}
                                  <p className="text-xs text-green-500 bg-transparent text-nowrap">
                                    {successMessage.queryId ===
                                      group.parent.id && successMessage.message}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* ---------- CHILD QUERIES (Indened + lighter) ---------- */}
                        {group.children.map((c, cIdx) => {
                          const childIndex = form.queries.findIndex(
                            (x) => x.id === c.id
                          );

                          return (
                            <div
                              key={cIdx}
                              className="flex flex-col gap-0 p-2 mb-5 ml-6 bg-gray-100 border border-blue-200 rounded-lg shadow-lg"
                            >
                              <div className="flex items-center gap-1 mb-2 bg-transparent">
                                {hasPermission("rfi", "delete") &&
                                  !response && (
                                    <input
                                      type="checkbox"
                                      style={{ cursor: "pointer" }}
                                      checked={rfiQueryToDelete.includes(c.id)}
                                      onChange={(e) =>
                                        handleSetDeleteQuery(
                                          e,
                                          c.id,
                                          groupedQueries
                                        )
                                      }
                                    />
                                  )}
                                <h5 className="m-0 bg-transparent">
                                  {c.qNo && (
                                    <p className="m-0 font-semibold bg-transparent">
                                      {c?.qNo?.split(" ")[0]}{" "}
                                      <span className="text-xs font-normal text-black bg-transparent">
                                        R
                                        {c?.qNo?.split(" ")[1].replace("R", "")}
                                      </span>
                                    </p>
                                  )}
                                </h5>
                                <span className="mt-1 ml-0.5 text-xs text-gray-500 bg-transparent">
                                  Type # to mention the file name in the query.
                                </span>
                              </div>
                              {showFileDropdown &&
                                activeQueryIdx === childIndex &&
                                fileList.length > 0 && (
                                  <div className="relative top-0 left-0 z-50 w-64 mt-1 overflow-auto bg-white border rounded shadow max-h-40">
                                    {fileList.map((file, i) => (
                                      <div
                                        key={i}
                                        className="px-2 py-1 text-sm cursor-pointer hover:bg-gray-100"
                                        onMouseDown={(e) => {
                                          e.preventDefault(); // prevent textarea blur
                                          insertFileReference(
                                            childIndex,
                                            file.file_name
                                          );
                                          setShowFileDropdown(false); // hide after selection
                                        }}
                                      >
                                        {file.file_name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              <textarea
                                value={c.query_text}
                                placeholder="Query"
                                // onChange={(e) =>
                                //   handleQueryChange(
                                //     childIndex,
                                //     "query_text",
                                //     e.target.value
                                //   )
                                // }
                                onChange={(e) => handleInput(e, childIndex)}
                                ref={(el) =>
                                  (textareaRefs.current[childIndex] = el)
                                }
                                disabled={
                                  c.status === "Closed" ||
                                  response ||
                                  !hasPermission("rfi", "update")
                                }
                                onInput={autoResize}
                                className={`auto-resize pl-[5px] border border-[#ddd] border-[0.5px] no-scrollbar rounded-[3px] font-lato text-[15px] w-auto min-h-0 h-auto max-h-auto focus:border-[#535353] focus:outline-none disabled:bg-white disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-white  ${
                                  c.status === "Closed" ||
                                  response ||
                                  (rfi && !hasPermission("rfi", "update"))
                                    ? "resize-none"
                                    : ""
                                }`}
                              />

                              {/* Child responses — same UI as parent */}
                              {rfi && (
                                <div className="p-2 pt-0 pb-2 mt-2 ml-5 bg-white rounded-lg">
                                  {c.responses.map((r, rIdx) => (
                                    <div
                                      key={rIdx}
                                      className="flex flex-col gap-2 py-2 bg-transparent"
                                    >
                                      <div className="flex items-center gap-4 bg-transparent">
                                        <h5 className="m-0 bg-transparent">
                                          Responded by
                                        </h5>

                                        <div
                                          className={`flex justify-between items-center  bg-transparent `}
                                        >
                                          {c.status === "Closed" ||
                                          (r.id &&
                                            !hasPermission("rfi", "update") &&
                                            r.entered_by !== userDetails.id) ? (
                                            // When disabled → show plain text
                                            <span className="w-auto text-[#0057D9] mr-2 text-[13px] whitespace-nowrap">
                                              {r.responded_by || ""}
                                            </span>
                                          ) : (
                                            <input
                                              value={r.responded_by}
                                              onChange={(e) =>
                                                handleResponseChange(
                                                  childIndex, // ← correct for child
                                                  rIdx,
                                                  "responded_by",
                                                  e.target.value
                                                )
                                              }
                                              disabled={
                                                c.status === "Closed" || // Block everyone if closed
                                                (!hasPermission(
                                                  "rfi",
                                                  "update"
                                                ) && // If NOT admin
                                                  r.id &&
                                                  r.entered_by !==
                                                    userDetails.id) // Only allow editing own response
                                              }
                                              className=" p-[6px] border-[0.5px] border-solid border-[#ddd] w-[15vw] rounded-[3px] font-lato text-[13px] cursor-pointer focus:border-[#535353] focus:outline-none mr-2"
                                              placeholder="Responded by"
                                            />
                                          )}
                                          {c.status === "Closed" ||
                                          (r.id &&
                                            !hasPermission("rfi", "update") &&
                                            r.entered_by !== userDetails.id) ? (
                                            // 🔒 Disabled → show plain text (no input)
                                            <span className=" text-[#E4572E] text-[13px] mr-2 whitespace-nowrap">
                                              {r.responded_date
                                                ? format(
                                                    r.responded_date,
                                                    "dd-MM-yyyy"
                                                  )
                                                : ""}
                                            </span>
                                          ) : (
                                            <DatePicker
                                              selected={r.responded_date}
                                              onChange={(date) =>
                                                handleResponseChange(
                                                  childIndex,
                                                  rIdx,
                                                  "responded_date",
                                                  date
                                                )
                                              }
                                              disabled={
                                                c.status === "Closed" || // Block everyone if closed
                                                (!hasPermission(
                                                  "rfi",
                                                  "update"
                                                ) &&
                                                  r.id &&
                                                  r.entered_by !==
                                                    userDetails.id) // Only allow editing own response
                                              }
                                              placeholderText="Select Date"
                                              dateFormat="dd-MM-yyyy"
                                              className="pl-[5px] border-[0.5px] border-solid border-[#ddd] h-[26px] w-[10vw] rounded-[3px] font-lato text-[13px] focus:border-[#535353] focus:outline-none mr-2"
                                            />
                                          )}
                                          {c.status === "Closed" ||
                                          (r.id &&
                                            !hasPermission("rfi", "update") &&
                                            r.entered_by !== userDetails.id) ? (
                                            <span className="  text-[#0E9F6E] text-[13px] mr-2 whitespace-nowrap">
                                              {r.discipline_name}
                                            </span>
                                          ) : (
                                            <select
                                              value={r.discipline_id}
                                              onChange={(e) =>
                                                handleResponseChange(
                                                  childIndex,
                                                  rIdx,
                                                  "discipline_id",
                                                  e.target.value
                                                )
                                              }
                                              disabled={
                                                c.status === "Closed" || // Block everyone if closed
                                                (!hasPermission(
                                                  "rfi",
                                                  "update"
                                                ) &&
                                                  r.id &&
                                                  r.entered_by !==
                                                    userDetails.id) // Only allow editing own response
                                              }
                                              className={`p-[5px] border border-[#ddd] border-[0.5px] h-[30px] w-[15vw] rounded-[3px] focus:border-[#535353] focus:outline-none font-lato text-[13px] cursor-pointer  disabled:bg-white  disabled:text-gray-500   disabled:cursor-not-allowed   disabled:border-white disabled:w-[auto]  mr-2 ${
                                                c.status === "Closed" ||
                                                (r.id &&
                                                  !hasPermission(
                                                    "rfi",
                                                    "update"
                                                  ))
                                                  ? "auto-select w-auto"
                                                  : "w-[15vw]"
                                              }`}
                                              required
                                            >
                                              <option>Select Discipline</option>
                                              {disciplines.map((d) => (
                                                <option key={d.id} value={d.id}>
                                                  {d.name}
                                                </option>
                                              ))}
                                            </select>
                                          )}
                                          {c.status === "Closed" ||
                                          (r.id &&
                                            !hasPermission("rfi", "update") &&
                                            r.entered_by !== userDetails.id) ? (
                                            // Show plain text when disabled
                                            <span className="text-[13px]  text-[#7B2CBF] font-lato ">
                                              {r.company_name || ""}
                                            </span>
                                          ) : (
                                            <input
                                              value={r.company_name}
                                              onChange={(e) =>
                                                handleResponseChange(
                                                  childIndex,
                                                  rIdx,
                                                  "company_name",
                                                  e.target.value
                                                )
                                              }
                                              disabled={
                                                c.status === "Closed" || // Block everyone if closed
                                                (!hasPermission(
                                                  "rfi",
                                                  "update"
                                                ) &&
                                                  r.id &&
                                                  r.entered_by !==
                                                    userDetails.id) // Only allow editing own response
                                              }
                                              className="resize-input p-[6px] border-[0.5px] border-solid border-[#ddd] w-[15vw] rounded-[3px] font-lato text-[13px] focus:border-[#535353] focus:outline-none mr-2"
                                              placeholder="Company Name"
                                            />
                                          )}
                                        </div>
                                      </div>

                                      <textarea
                                        value={r.response}
                                        onChange={(e) =>
                                          handleResponseChange(
                                            childIndex,
                                            rIdx,
                                            "response",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          c.status === "Closed" || // Block everyone if closed
                                          (!hasPermission("rfi", "update") &&
                                            r.id &&
                                            r.entered_by !== userDetails.id) // Only allow editing own response
                                        }
                                        onInput={autoResize}
                                        placeholder="Response"
                                        className={`auto-resize pl-[5px] border border-[#ddd] border-[0.5px] no-scrollbar rounded-[3px] font-lato text-[15px] w-auto min-h-0 h-auto max-h-auto focus:border-[#535353] focus:outline-none disabled:bg-white disabled:text-gray-500 disabled:cursor-not-allowed disabled:border-white ${
                                          c.status === "Closed" ||
                                          response ||
                                          (rfi &&
                                            !hasPermission("rfi", "update"))
                                            ? "resize-none"
                                            : ""
                                        }`}
                                      />
                                    </div>
                                  ))}

                                  <div className="flex gap-2 w-[600px] bg-transparent">
                                    {c.revision !== 0 && (
                                      <select
                                        value={c.status}
                                        onChange={(e) =>
                                          handleQueryChange(
                                            childIndex,
                                            "status",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          response ||
                                          !hasPermission("rfi", "update")
                                        }
                                        className="p-[6px] border-[0.5px] border-solid border-[#ddd] w-[30%]  rounded-[3px] font-lato text-[14px] cursor-pointer focus:border-[#535353] focus:outline-none"
                                      >
                                        <option value="Open">Open</option>
                                        <option value="Closed">Closed</option>
                                      </select>
                                    )}
                                    {c.status !== "Closed" &&
                                      !c.responses?.some((r) => !r.id) && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            addResponse(childIndex)
                                          }
                                          disabled={c.status === "Closed"}
                                          className={`h-auto px-[10px] py-[6px] text-center border-none text-white rounded-[3px] font-lato text-[13px] ${
                                            c.status === "Closed"
                                              ? "bg-gray-500 cursor-not-allowed"
                                              : "bg-[#6200ee] cursor-pointer"
                                          }`}
                                        >
                                          Add Response
                                        </button>
                                      )}
                                    {c.status === "Open" &&
                                      successMessage.queryId !== c.id &&
                                      c.responses?.some(
                                        (r) =>
                                          !r.id &&
                                          r.responded_by &&
                                          r.responded_date &&
                                          r.response &&
                                          r.discipline_id &&
                                          r.company_name
                                      ) && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleResponse(c.id);
                                            !hasPermission("rfi", "update") &&
                                              setOpenDialog(false);
                                          }}
                                          disabled={c.status === "Closed"}
                                          className={`h-auto  px-[10px] py-[6px] text-center border-none text-white rounded-[3px] font-lato text-[13px] ${
                                            c.status === "Closed"
                                              ? "bg-gray-500 cursor-not-allowed"
                                              : "bg-[#6200ee] cursor-pointer"
                                          }`}
                                        >
                                          Submit
                                        </button>
                                      )}
                                    <p className="text-xs text-green-500 bg-transparent text-nowrap">
                                      {successMessage.queryId === c.id &&
                                        successMessage.message}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                </div>

                <div className="rfi-Query-con">
                  {!response && (
                    <button
                      type="button"
                      onClick={addQuery}
                      className="add-query-btn"
                    >
                      Add Query
                    </button>
                  )}
                  {!response &&
                    (rfi
                      ? form.queries.length > rfi.queries.length // editing
                      : form.queries.length > 1) && ( // creating
                      <button
                        type="button"
                        onClick={removeQuery}
                        className="remove-query-btn"
                      >
                        Remove Query
                      </button>
                    )}
                </div>
              </div>
              <div className="  border-t border-[#616161] pt-0 h-[50px] flex justify-between items-center px-4  w-[98.2%] bg-white fixed bottom-0 left-0 border-b-0 border-solid border-x-0">
                <div className="flex gap-4">
                  {rfi
                    ? hasPermission("rfi", "update") && (
                        <button
                          type="submit"
                          className="h-auto w-auto px-[10px] py-[6px] text-center border-none text-white rounded-[3px] bg-[#6200ee] cursor-pointer font-lato text-[13px]"
                        >
                          Update
                        </button>
                      )
                    : hasPermission("rfi", "create") && (
                        <button
                          type="submit"
                          className="h-auto w-auto px-[10px] py-[6px] text-center border-none text-white rounded-[3px] bg-[#6200ee] cursor-pointer font-lato text-[13px]"
                        >
                          Create
                        </button>
                      )}
                  <button
                    onClick={() => {
                      setOpenDialog(false);
                      setEditingRfi(null);
                      setDeleteQueryDialogOpen(false);
                      setRfiQueryToDelete([]);
                      setIsQueryCheckboxChecked(false);
                      resetForm();
                      onSuccess();
                    }}
                    className="h-auto w-auto py-[5px] px-[10px] text-center text-[#6200ee] rounded-[3px] font-normal border border-[#6200ee] bg-transparent cursor-pointer font-lato text-[13px]"
                  >
                    Cancel
                  </button>
                </div>
                {rfi && (
                  <div className="flex bg-transparent">
                    <h3 className="text-sm font-normal">
                      Total : {rfi.queries.length}
                    </h3>
                    <select
                      className="border border-white/0 p-1 rounded text-center font-lato text-sm cursor-pointer outline-none bg-transparent ml-1.5"
                      value={rfiQueriesPerPage}
                      onChange={(e) => {
                        setRfiQueriesPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="5">5 Records Per Page</option>
                      <option value="10">10 Records Per Page</option>
                      <option value="15">15 Records Per Page</option>
                      <option value="20">20 Records Per Page</option>
                    </select>
                    <button
                      onClick={handleFirstPage}
                      disabled={currentPage === 1}
                      className="border-none mt-[0px] cursor-pointer bg-transparent"
                    >
                      <MdKeyboardDoubleArrowLeft title="First Page" />
                    </button>
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="border-none mt-[0px] cursor-pointer bg-transparent"
                    >
                      {/* */}
                      <MdKeyboardArrowLeft title="Previous Page" />
                    </button>
                    <span className="mt-[12px]">{paginationCount}</span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="border-none mt-[0px] cursor-pointer bg-transparent"
                    >
                      <MdKeyboardArrowRight title="Next Page" />
                    </button>
                    <button
                      onClick={handleLastPage}
                      disabled={currentPage === totalPages}
                      className="border-none mt-[0px] cursor-pointer bg-transparent"
                    >
                      <MdKeyboardDoubleArrowRight title="Last Page" />
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
      {deleteQueryDialogOpen && (
        <div className="delete-phase-dial" style={{ zIndex: "1000" }}>
          <div className="delete-dial">
            <div className="delete-header">
              <h3
                style={{
                  backgroundColor: "transparent",
                  marginLeft: "15px",
                  fontSize: "15px",
                }}
              >
                Delete RFI Query
              </h3>
            </div>
            <div style={{ backgroundColor: "white" }}>
              <p style={{ backgroundColor: "white" }}>
                Are you sure you want to delete this Rfi query ? This action
                cannot be undone.
              </p>
              <input
                style={{ cursor: "pointer" }}
                type="checkbox"
                id="delete-phase"
                checked={isQueryCheckboxChecked}
                onChange={() => setIsQueryCheckboxChecked((prev) => !prev)}
              />
              <label
                htmlFor="delete-phase"
                style={{ backgroundColor: "white", cursor: "pointer" }}
              >
                I understand this action cannot be undone.
              </label>
            </div>
            <div className="delete-dial-btn-con">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => handleDeleteQuery(rfiQueryToDelete)}
                color="secondary"
                variant="contained"
                disabled={!isQueryCheckboxChecked}
                className="add-btn"
                style={{
                  backgroundColor: isQueryCheckboxChecked ? "red" : "gray",
                }}
              >
                Delete
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setDeleteQueryDialogOpen(false);
                  setIsQueryCheckboxChecked(false);
                  setResponse();
                  setRfiQueryToDelete([]);
                  setShowFileDropdown(false);
                }}
                className="cancel-btn"
              >
                Cancel
              </motion.button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
